import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Banknote, ArrowUpDown, ArrowRight, Search, AlertTriangle, Landmark,
  TrendingUp, Layers, Calculator, BookOpen, Percent, ArrowLeftRight,
} from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import { fmtPct, fmtAum, fmtDate } from '@/components/coveredCall/ccFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Money market fund hub — /money-market-fund-yields
 *
 * Money funds are not stocks: no payout history, no frequency, no
 * dividend yield. The page shows the three fields we actually hold —
 * 7-day SEC yield, fund size and the refresh timestamp — plus the one
 * comparison nobody publishes next to them: the tax-equivalent yield a
 * municipal fund has to beat.
 */
const COLUMNS = [
  { key: 'ticker', label: 'Fund', align: 'left' },
  { key: 'family', label: 'Family', align: 'left' },
  { key: 'category', label: 'Type', align: 'left' },
  { key: 'seven_day_yield', label: '7-day yield' },
  { key: 'expense_ratio', label: 'Expense' },
  { key: 'aum', label: 'Fund size' },
  { key: 'weekly_liquid_pct', label: 'Weekly liquid' },
];

const BRACKETS = [
  { label: 'No adjustment', rate: 0 },
  { label: '22% federal', rate: 22 },
  { label: '24% federal', rate: 24 },
  { label: '32% federal', rate: 32 },
  { label: '35% federal', rate: 35 },
  { label: '37% federal', rate: 37 },
  { label: '37% + 3.8% NIIT', rate: 40.8 },
];

export default function MoneyMarketFunds() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [bracket, setBracket] = useState(0);
  const [sort, setSort] = useState({ key: 'seven_day_yield', dir: 'desc' });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/money-market-funds`)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useSeoMeta({
    title: 'Money market fund yields — 7-day SEC yield compared | DiviDrip',
    description: 'Current 7-day SEC yields and expense ratios for the money market funds US brokers actually offer — SPAXX, VMFXX, SWVXX, VUSXX, SNSXX and more — straight from each fund\u2019s SEC filings, split by government, Treasury, prime and municipal, with tax-equivalent yields and the money market ETFs alongside.',
    canonical: 'https://twylightcrow.com/money-market-fund-yields',
  });

  const rows = data?.rows || [];
  const s = data?.stats;
  const taxRate = bracket / 100;

  const withTey = useMemo(() => rows.map((r) => ({
    ...r,
    tey: r.category === 'Municipal' && taxRate
      ? r.seven_day_yield / (1 - taxRate)
      : null,
  })), [rows, taxRate]);

  const visible = useMemo(() => {
    const needle = q.trim().toUpperCase();
    const out = withTey.filter((r) => {
      if (cat !== 'all' && r.category !== cat) return false;
      if (!needle) return true;
      return r.ticker.includes(needle)
        || (r.name || '').toUpperCase().includes(needle)
        || (r.family || '').toUpperCase().includes(needle);
    });
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      const av = a[key]; const bv = b[key];
      if (['ticker', 'family', 'category'].includes(key)) {
        return String(av || '').localeCompare(String(bv || '')) * mult;
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * mult;
    });
  }, [withTey, q, cat, sort]);

  const faqs = useMemo(() => (s ? buildFaqs(s, data) : []), [s, data]);

  useEffect(() => {
    if (!data) return;
    const block = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.text = JSON.stringify(block);
    el.setAttribute('data-testid', 'mmf-jsonld');
    document.head.appendChild(el);
    return () => el.remove();
  }, [data, faqs]);

  const toggleSort = (key) => setSort((st) => (
    st.key === key ? { key, dir: st.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: ['ticker', 'family', 'category'].includes(key) ? 'asc' : 'desc' }
  ));

  // The category cards sit well above the table, so a filter that only
  // changed rows off-screen read as a dead click.
  const selectCategory = (next) => {
    setCat((prev) => (prev === next ? 'all' : next));
    document.querySelector('[data-testid="mmf-table-section"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 space-y-4 animate-pulse" data-testid="mmf-loading">
        <div className="h-10 w-2/3 bg-slate-200 dark:bg-slate-800 rounded" />
        <div className="h-40 bg-slate-200 dark:bg-slate-800 rounded-xl" />
      </div>
    );
  }

  if (!data || !s?.count) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="mmf-error">
        <BackOrHomeButton />
        <h1 className="mt-6 text-3xl font-black text-slate-900 dark:text-slate-100">Data unavailable</h1>
        <SiteDisclaimer />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="mmf-page">
      <BackOrHomeButton />

      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-400 font-semibold">
        <Banknote className="w-3.5 h-3.5" />
        <Link to="/dividend-stocks-by-sector" className="hover:underline" data-testid="mmf-breadcrumb">Income research</Link>
      </div>

      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-slate-100">
        Money market fund <span className="text-cyan-600 dark:text-cyan-400">yields</span>
      </h1>
      <p className="mt-4 max-w-3xl text-base text-slate-600 dark:text-slate-400 leading-relaxed">
        The cash sitting in your brokerage account is a fund with a yield, and the spread between
        the best and worst of them is wider than most dividend decisions you'll make this year.
        These are the 7-day SEC yields on the money funds we track, split by what they actually
        hold — <strong className="text-slate-900 dark:text-slate-100">government, Treasury, prime
        and municipal</strong> — because those four behave very differently after tax.
      </p>
      <p className="mt-3 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
        {s.count} funds from {s.families} fund families, median 7-day yield{' '}
        <strong className="text-slate-900 dark:text-slate-100">{fmtPct(s.median_yield)}</strong>.
        {s.report_date && <> Straight from SEC Form N-MFP for the period ending {fmtDate(s.report_date)}.</>}
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mmf-stats">
        <Stat label="Funds tracked" value={s.count} testId="mmf-stat-count" />
        <Stat label="Median 7-day yield" value={fmtPct(s.median_yield)} testId="mmf-stat-median" />
        <Stat
          label="Highest 7-day yield"
          value={`${s.top_yield_ticker} · ${fmtPct(s.top_yield_value)}`}
          testId="mmf-stat-top"
        />
        <Stat
          label="Combined fund size"
          value={fmtAum(s.total_aum)}
          hint={`Across the ${s.sized} funds we can size`}
          testId="mmf-stat-aum"
        />
      </div>

      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400" data-testid="mmf-coverage">
        {s.taxable_count} taxable funds and {s.municipal_count} municipal funds.{' '}
        {s.sec_sourced} of the {s.count} figures come straight from each fund&apos;s
        SEC <strong>Form N-MFP</strong> filing — the monthly report every US money fund
        has to file, share class by share class — rather than a data vendor&apos;s guess.
        {s.nav_max != null && ` Every one of them still priced within $${(Math.max(Math.abs(s.nav_max - 1), Math.abs(1 - s.nav_min))).toFixed(4)} of $1.00 on the filing date.`}
        {!!s.excluded && ` ${s.excluded} fund in our database is held back because no
        current yield reading exists for it — a wrong number on a cash page is worse
        than a missing fund.`}
      </p>

      {data.fee_gap && (
        <section className="mt-10 p-6 rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/50 dark:bg-emerald-950/20" data-testid="mmf-fee-gap">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-bold text-emerald-700 dark:text-emerald-400">
            <Percent className="w-3.5 h-3.5" /> The only thing that really separates them
          </div>
          <p className="mt-3 text-base text-slate-800 dark:text-slate-200 leading-relaxed">
            Money funds all buy the same short-dated paper, so the fee is most of the difference.
            The {data.fee_gap.cheap_count} funds here charging{' '}
            <strong>{fmtPct(0.2)} or less</strong> pay a median{' '}
            <strong className="text-emerald-700 dark:text-emerald-400">{fmtPct(data.fee_gap.cheap_median)}</strong>.
            The {data.fee_gap.pricey_count} charging <strong>{fmtPct(0.5)} or more</strong> pay a median{' '}
            <strong className="text-rose-600 dark:text-rose-400">{fmtPct(data.fee_gap.pricey_median)}</strong>{' '}
            — a gap of <strong>{data.fee_gap.gap_bps} basis points</strong> on money that is
            supposed to be the boring part of the portfolio. On $50,000 of cash that is about{' '}
            <strong>${Math.round(50000 * data.fee_gap.gap_bps / 10000).toLocaleString()} a year</strong>.
          </p>
          <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
            The 7-day yield is already net of fees, so don&apos;t subtract the expense ratio
            again — it explains the yield rather than reducing it. Cheapest fund on this page:{' '}
            <span className="font-mono font-bold">{data.fee_gap.cheapest}</span>. Expense ratios
            come from each fund&apos;s SEC N-CSR shareholder report and cover{' '}
            {s.expense_coverage} of the {s.count} share classes; median {fmtPct(s.median_expense)}.
          </p>
        </section>
      )}

      {/* Categories */}
      <section className="mt-12" data-testid="mmf-categories">        <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
          The four kinds of money fund
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Same $1.00 share price, very different portfolios — and very different tax treatment.
          Tap a card to filter the table below to that group.
        </p>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {data.categories.map((c) => (
            <button
              key={c.category}
              onClick={() => selectCategory(c.category)}
              className={`text-left p-5 rounded-xl border transition-colors ${cat === c.category
                ? 'border-cyan-500 bg-cyan-50/60 dark:bg-cyan-950/20'
                : 'border-slate-200 dark:border-slate-800 hover:border-cyan-400 dark:hover:border-cyan-700'}`}
              data-testid={`mmf-category-${c.category.toLowerCase()}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100">
                  <Landmark className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> {c.category}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
                  {c.count} funds · median {fmtPct(c.median_yield)}
                </span>
              </div>
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">{c.note}</p>
              <p className="mt-2 text-xs font-semibold text-slate-700 dark:text-slate-300">
                Top of the group: <span className="font-mono">{c.top_yield_ticker}</span> at {fmtPct(c.top_yield_value)}
              </p>
              <p className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-cyan-700 dark:text-cyan-400">
                {cat === c.category
                  ? `Showing these ${c.count} — tap to clear`
                  : `Show the ${c.count} ${c.category.toLowerCase()} funds`}
                <ArrowRight className="w-3 h-3" />
              </p>
            </button>
          ))}
        </div>
      </section>

      {/* Cuts */}
      <section className="mt-12 grid md:grid-cols-2 gap-4 min-w-0" data-testid="mmf-cuts">
        <CutCard
          icon={TrendingUp}
          title="Highest 7-day yield"
          rows={data.cuts.highest_yield}
          metric={(r) => fmtPct(r.seven_day_yield)}
          testId="mmf-cut-yield"
        />
        <CutCard
          icon={Layers}
          title="Largest by fund size"
          rows={data.cuts.largest}
          metric={(r) => `${fmtAum(r.aum)} · ${fmtPct(r.seven_day_yield)}`}
          testId="mmf-cut-largest"
        />
      </section>

      {/* Table */}
      <section className="mt-12" data-testid="mmf-table-section">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
              {cat === 'all'
                ? `All ${s.count} money market funds`
                : `${cat} money market funds`}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {visible.length} shown · click any column to sort
              {cat !== 'all' && (
                <button
                  onClick={() => setCat('all')}
                  className="ml-2 font-semibold text-cyan-700 dark:text-cyan-400 hover:underline"
                  data-testid="mmf-clear-filter"
                >clear filter</button>
              )}
            </p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ticker, fund or family…"
              className="pl-9 pr-3 py-2 w-64 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              data-testid="mmf-search"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="mmf-filters">
          <Chip label="All types" active={cat === 'all'} onClick={() => setCat('all')} testId="mmf-filter-all" />
          {data.categories.map((c) => (
            <Chip
              key={c.category}
              label={`${c.category} (${c.count})`}
              active={cat === c.category}
              onClick={() => setCat(c.category)}
              testId={`mmf-filter-${c.category.toLowerCase()}`}
            />
          ))}
        </div>

        <div className="mt-4 p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40" data-testid="mmf-tey">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-slate-100">
              <Calculator className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
              Tax-equivalent yield
            </span>
            <select
              value={bracket}
              onChange={(e) => setBracket(Number(e.target.value))}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              data-testid="mmf-tey-bracket"
            >
              {BRACKETS.map((b) => (
                <option key={b.rate} value={b.rate}>{b.label}</option>
              ))}
            </select>
          </div>
          <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
            {taxRate ? (
              <>
                A municipal fund's income is federally tax-exempt, so at {bracket}% its{' '}
                {fmtPct(data.categories.find((c) => c.category === 'Municipal')?.median_yield)} median
                is worth{' '}
                <strong className="text-slate-900 dark:text-slate-100">
                  {fmtPct((data.categories.find((c) => c.category === 'Municipal')?.median_yield || 0) / (1 - taxRate))}
                </strong>{' '}
                from a taxable fund. The <em>TEY</em> column below shows that for each municipal fund.
              </>
            ) : (
              <>Pick your federal bracket to see what each municipal fund's tax-free yield is
              worth against a taxable fund. At lower brackets the taxable funds usually win; in the
              top bracket it is often the other way round.</>
            )}
          </p>
        </div>

        <p className="mt-3 sm:hidden text-[11px] text-slate-500 dark:text-slate-400" data-testid="mmf-scroll-hint">
          Swipe the table sideways to see yield, fee and fund size →
        </p>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm" data-testid="mmf-table">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 cursor-pointer select-none hover:text-cyan-600 dark:hover:text-cyan-400 whitespace-nowrap ${c.align === 'left' ? 'text-left' : 'text-right'}`}
                    data-testid={`mmf-th-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sort.key === c.key && <ArrowUpDown className="w-3 h-3 text-cyan-500" />}
                    </span>
                  </th>
                ))}
                {!!taxRate && (
                  <th
                    onClick={() => toggleSort('tey')}
                    className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 cursor-pointer select-none whitespace-nowrap"
                    data-testid="mmf-th-tey"
                  >
                    TEY @ {bracket}%
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.ticker}
                  className="border-t border-slate-100 dark:border-slate-800/70 hover:bg-slate-50 dark:hover:bg-slate-900/40"
                  data-testid={`mmf-row-${r.ticker}`}
                >
                  <td className="px-3 py-2.5">
                    <Link
                      to={`/stock/${r.ticker}`}
                      className="font-mono font-bold text-slate-900 dark:text-slate-100 hover:text-cyan-600 dark:hover:text-cyan-400"
                      data-testid={`mmf-ticker-link-${r.ticker}`}
                    >
                      {r.ticker}
                    </Link>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[280px]">{r.name}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{r.family || '—'}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${r.category === 'Municipal'
                      ? 'bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300'
                      : r.category === 'Prime'
                        ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
                      {r.category}
                    </span>
                    {r.state && <span className="ml-1 text-[10px] text-slate-400">{r.state}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold tabular-nums text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                    {fmtPct(r.seven_day_yield)}
                    {r.yield_suspect && (
                      <AlertTriangle
                        className="inline w-3 h-3 ml-1 text-amber-500"
                        title="Out of line with every other money fund — treat this reading as stale until it refreshes"
                        data-testid={`mmf-suspect-${r.ticker}`}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap" title={r.expense_ratio == null ? 'No current shareholder report parsed for this share class' : 'Net expense ratio from the fund\u2019s latest SEC N-CSR shareholder report'}>
                    {r.expense_ratio == null ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <span className={r.expense_ratio <= 0.2
                        ? 'text-emerald-600 dark:text-emerald-400 font-semibold tabular-nums'
                        : r.expense_ratio >= 0.5
                          ? 'text-rose-600 dark:text-rose-400 font-semibold tabular-nums'
                          : 'text-slate-600 dark:text-slate-400 tabular-nums'}>
                        {fmtPct(r.expense_ratio)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap" title={r.fund_aum && r.fund_aum !== r.aum ? `Share class of a ${fmtAum(r.fund_aum)} fund` : undefined}>
                    {r.aum ? fmtAum(r.aum) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap" title="Assets convertible to cash within five business days. SEC Rule 2a-7 sets a 50% floor.">
                    {r.weekly_liquid_pct == null ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <span className={r.weekly_liquid_pct >= 90
                        ? 'text-emerald-600 dark:text-emerald-400 font-semibold tabular-nums'
                        : r.weekly_liquid_pct <= 55
                          ? 'text-amber-600 dark:text-amber-400 font-semibold tabular-nums'
                          : 'text-slate-600 dark:text-slate-400 tabular-nums'}>
                        {fmtPct(r.weekly_liquid_pct, 0)}
                      </span>
                    )}
                  </td>
                  {!!taxRate && (
                    <td className="px-3 py-2.5 text-right font-bold tabular-nums text-violet-700 dark:text-violet-300 whitespace-nowrap">
                      {r.tey != null ? fmtPct(r.tey) : '—'}
                    </td>
                  )}
                </tr>
              ))}
              {!visible.length && (
                <tr>
                  <td colSpan={COLUMNS.length + (taxRate ? 1 : 0)} className="px-3 py-8 text-center text-slate-500" data-testid="mmf-empty">
                    No money market funds match that filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          The 7-day SEC yield is the standardised measure — annualised income over seven days,
          net of the fund&apos;s fees — so it is the only figure that compares two money funds
          fairly. It moves with the Fed, not with the fund. Fund size is the{' '}
          <strong>share class</strong> figure, which is what your ticker actually buys; hover a
          size to see the whole fund where the two differ. Weekly liquid assets is the share of
          the portfolio convertible to cash within five business days — SEC Rule 2a-7 sets a 50%
          floor, and a Treasury-only fund typically runs near 100%. Money funds are not
          FDIC-insured and are not guaranteed to hold $1.00 a share, though only two US funds
          ever have not.
        </p>
      </section>

      {/* Money market ETFs — a different product, kept apart on purpose */}
      {!!data.etfs?.length && (
        <section className="mt-16 p-6 rounded-xl border-2 border-dashed border-amber-300 dark:border-amber-900/70 bg-amber-50/40 dark:bg-amber-950/10" data-testid="mmf-etfs">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-bold text-amber-700 dark:text-amber-500">
            <ArrowLeftRight className="w-3.5 h-3.5" /> Related, but not the same thing
          </div>
          <h2 className="mt-2 text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
            Money market ETFs
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            These {data.etfs.length} are exchange-traded, so everything above changes: they trade
            intraday at a market price near <strong>$100 a share</strong> rather than a fixed
            $1.00, the NAV floats, and no broker will sweep your idle cash into one — you buy them
            like any ETF. What they publish is a <strong>trailing distribution yield</strong>, not
            a 7-day SEC yield, so the numbers below are <em>not</em> directly comparable with the
            table above. They are listed here because they answer the same question — where does
            cash sit — and because they settle like a stock, which suits money you may want to
            move into a position on the same day.
          </p>
          <p className="mt-3 sm:hidden text-[11px] text-slate-600 dark:text-slate-400" data-testid="mmf-etf-scroll-hint">
            Swipe the table sideways to see price, yield and fee →
          </p>
          <div className="mt-4 overflow-x-auto rounded-lg border border-amber-200 dark:border-amber-900/60 bg-white dark:bg-slate-900/60">
            <table className="w-full text-sm" data-testid="mmf-etf-table">
              <thead className="bg-amber-50 dark:bg-slate-900">
                <tr>
                  {['Fund', 'Price', 'Distribution yield', 'Pays', 'Expense', 'Fund size'].map((h, i) => (
                    <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 whitespace-nowrap ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.etfs.map((e) => (
                  <tr key={e.ticker} className="border-t border-amber-100 dark:border-slate-800" data-testid={`mmf-etf-row-${e.ticker}`}>
                    <td className="px-3 py-2.5">
                      <Link to={`/stock/${e.ticker}`} className="font-mono font-bold text-slate-900 dark:text-slate-100 hover:text-cyan-600 dark:hover:text-cyan-400">{e.ticker}</Link>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[300px]">{e.name}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 dark:text-slate-300">{e.price ? `$${e.price.toFixed(2)}` : '—'}</td>
                    <td className="px-3 py-2.5 text-right font-bold tabular-nums text-emerald-600 dark:text-emerald-400">{fmtPct(e.distribution_yield)}</td>
                    <td className="px-3 py-2.5 text-right text-xs text-slate-600 dark:text-slate-400">{e.frequency || '—'}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-400">{e.expense_ratio == null ? '—' : fmtPct(e.expense_ratio)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400">{e.aum ? fmtAum(e.aum) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-slate-600 dark:text-slate-400">
            Prices as of {fmtDate(data.etfs[0].as_of)}. A distribution yield looks backwards over
            the last twelve months of payments, so it lags a change in short rates in the opposite
            direction to a 7-day yield — one is stale where the other is fresh. Treat the two
            tables as separate shortlists, not one ranking.
          </p>
        </section>
      )}

      {/* Education */}
      <section className="mt-12 grid md:grid-cols-2 gap-4" data-testid="mmf-education">
        <Note title="Why the same broker pays two different yields">
          Brokers auto-sweep uninvested cash into a default fund, and the default is almost never
          the best-yielding one they offer. Buying a money fund by ticker inside the same account
          is usually a two-minute trade, and the difference compounds on every dollar of cash you
          hold between dividend reinvestments.
        </Note>
        <Note title="Treasury funds and state tax">
          Interest from a Treasury-only fund is generally exempt from state income tax, while a
          government fund holding repo is only partly exempt. In a 9-13% state that can be worth
          more than the 5-10 basis points of headline yield you gave up to get it — check the
          fund's year-end tax letter for the exact percentage.
        </Note>
        <Note title="Municipal funds are not automatically better">
          Tax-free sounds decisive, but the market prices that exemption in. A municipal fund only
          wins once your federal bracket is high enough that its tax-equivalent yield clears the
          taxable funds — use the selector above rather than assuming.
        </Note>
        <Note title="Where cash fits in a dividend portfolio">
          A money fund is the parking spot between contributions and purchases, not a dividend
          holding. It pays interest, not qualified dividends, so it is taxed at your ordinary rate
          and does nothing for dividend growth — which is exactly why holding more of it than your
          plan calls for is expensive.
        </Note>
      </section>

      <section className="mt-12 p-6 rounded-xl bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-slate-900 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="mmf-cta">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Put the cash to work</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/dividend-stocks-by-sector" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="mmf-cta-sectors">
            <Layers className="w-4 h-4" /> Dividend stocks by sector
          </Link>
          <Link to="/dividend-reinvestment-calculator" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="mmf-cta-calc">
            <Calculator className="w-4 h-4" /> Reinvestment calculator
          </Link>
          <Link to="/learn/money-market-funds" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="mmf-cta-guide">
            <BookOpen className="w-4 h-4" /> Money market funds — the XX ticker guide
          </Link>
          <Link to="/screener" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="mmf-cta-screener">
            <Search className="w-4 h-4" /> Screener
          </Link>
        </div>
      </section>

      <section className="mt-12" data-testid="mmf-faq">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Frequently asked</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 group" data-testid={`mmf-faq-item-${i}`}>
              <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100 list-none flex items-start justify-between gap-3">
                <span>{f.q}</span>
                <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <SiteDisclaimer />
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */

function Stat({ label, value, hint, testId }) {
  return (
    <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 min-w-0" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-black tabular-nums text-slate-900 dark:text-slate-100 truncate">{value ?? '—'}</div>
      {hint && <div className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">{hint}</div>}
    </div>
  );
}

function Chip({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active
        ? 'bg-cyan-600 border-cyan-600 text-white'
        : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-cyan-400'}`}
      data-testid={testId}
    >{label}</button>
  );
}

function Note({ title, children }) {
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800">
      <h3 className="font-bold text-slate-900 dark:text-slate-100">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{children}</p>
    </div>
  );
}

function CutCard({ icon: Icon, title, rows, metric, testId }) {
  if (!rows?.length) return null;
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 min-w-0 overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100">
        <Icon className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> {title}
      </div>
      <ol className="mt-3 space-y-1.5">
        {rows.map((r, i) => (
          <li key={r.ticker} className="flex items-center justify-between gap-3 text-sm min-w-0">
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] tabular-nums text-slate-400 w-4 shrink-0">{i + 1}</span>
              <Link
                to={`/stock/${r.ticker}`}
                className="font-mono font-bold text-slate-900 dark:text-slate-100 shrink-0 hover:text-cyan-600 dark:hover:text-cyan-400"
                data-testid={`mmf-leader-link-${r.ticker}`}
              >
                {r.ticker}
              </Link>
              <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{r.family || r.name}</span>
            </span>
            <span className="shrink-0 tabular-nums font-semibold text-slate-800 dark:text-slate-200">{metric(r)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function buildFaqs(s, data) {
  const gov = data.categories.find((c) => c.category === 'Government');
  const tre = data.categories.find((c) => c.category === 'Treasury');
  const muni = data.categories.find((c) => c.category === 'Municipal');
  return [
    {
      q: 'What is a 7-day SEC yield?',
      a: 'It is the income the fund earned over the last seven days, net of its fees, annualised — the standardised figure every US money fund has to publish so two funds can be compared on the same basis. It is a snapshot of current conditions, not a projection: when the Fed moves, every money fund yield moves with it within a few weeks.',
    },
    {
      q: 'Which money market fund has the highest yield right now?',
      a: `${s.top_yield_ticker} at ${fmtPct(s.top_yield_value)}, against a median of ${fmtPct(s.median_yield)} across the ${s.count} funds tracked here${s.report_date ? ` on the ${fmtDate(s.report_date)} SEC filings` : ''}. Money funds all hold the same short-dated paper, so the spread between them is mostly a fee difference — which is why the cheapest share class you can access usually wins.`,
    },
    {
      q: 'Government, Treasury, prime or municipal — which should I hold?',
      a: `${gov ? `Government funds are the default at most brokers (${gov.count} here, median ${fmtPct(gov.median_yield)}). ` : ''}${tre ? `Treasury-only funds (${tre.count}, median ${fmtPct(tre.median_yield)}) give up a little yield for state-tax exemption in most states. ` : ''}Prime funds pay the most because they hold commercial paper and bank CDs, with a floating NAV for institutional share classes.${muni ? ` Municipal funds (${muni.count}, median ${fmtPct(muni.median_yield)}) only win once your federal bracket makes the tax exemption worth more than the yield you gave up.` : ''}`,
    },
    {
      q: 'Are money market ETFs the same as money market funds?',
      a: `No, and it matters. A money market ETF trades on an exchange at a floating market price around $100 a share, settles like a stock, and quotes a trailing distribution yield rather than a 7-day SEC yield. A money market fund is priced at a stable $1.00, can be your broker's automatic cash sweep, and quotes the standardised 7-day figure. ${data.etfs?.length ? `We list the ${data.etfs.length} money market ETFs we track in their own section, kept separate on purpose — putting the two yields in one ranking would compare a backward-looking number with a forward-looking one.` : ''}`,
    },
    {
      q: 'Does the expense ratio come off the yield I see?',
      a: `No — the 7-day SEC yield is already net of fees, so subtracting the expense ratio again double-counts it. What the fee does is explain the yield: these funds hold nearly identical paper, so a fund charging 0.60% simply has less left to pay out than one charging 0.10%.${data.fee_gap ? ` On this page the funds charging 0.20% or less pay a median ${fmtPct(data.fee_gap.cheap_median)} against ${fmtPct(data.fee_gap.pricey_median)} for those charging 0.50% or more — ${data.fee_gap.gap_bps} basis points of pure fee difference.` : ''} Expense ratios here are read from each fund's SEC N-CSR shareholder report.`,
    },
    {
      q: 'Is money market fund income a dividend?',
      a: 'It is paid as a dividend and shows up as one on your 1099-DIV, but it is not a qualified dividend — it is interest income in economic terms, and it is taxed at your ordinary income rate. That matters for a dividend portfolio: cash parked in a money fund is the least tax-efficient income you own, so it belongs in a tax-sheltered account if you are holding a lot of it.',
    },
    {
      q: 'Are money market funds safe?',
      a: `They are not FDIC-insured and not guaranteed. They aim to hold a stable $1.00 share price and government and Treasury funds are required to keep at least 99.5% of assets in cash, Treasuries and government repo, which is why they are treated as cash equivalents. Since the 2023 reforms every money fund must also keep at least 50% of the portfolio convertible to cash within a week — the median fund here reports ${fmtPct(s.median_weekly_liquid, 0)} weekly liquid assets, well clear of the floor. Two US funds have ever broken the buck. Institutional prime funds carry the most risk of the group: floating NAV plus the possibility of a liquidity fee in a stressed market.`,
    },
    {
      q: 'How current are these yields, and where do they come from?',
      a: `Each fund's own SEC Form N-MFP — the report every US money market fund files monthly, listing net assets, the 7-day net yield and the liquidity profile for each individual share class. ${s.sec_sourced} of the ${s.count} funds here are read straight from that filing${s.report_date ? ` for the period ending ${fmtDate(s.report_date)}` : ''}; the rest fall back to a market data vendor. That distinction matters more than it sounds: the vendor feed had the largest fund on this page sized at $1,000,000 (it holds over $400bn) and four funds yielding 0.01% when they were really paying over 3%. N-MFP is filed within five business days of month end, so the figures step forward monthly — the ranking holds between filings, but your broker's fund page is the authority for a trade today.`,
    },
  ];
}
